import { EnvironmentOutlined, ExportOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIncidentDetailLinkedContext } from '~/app/components/incident/IncidentDetailLinkedContext/hooks/useIncidentDetailLinkedContext';
import type { IncidentDetailLinkedContextData } from '~/app/components/incident/IncidentDetailLinkedContext/types';
import { getPublicAssetUrl } from '~/app/utils/getPublicAssetUrl';
import styles from './IncidentDetailLinkedContext.module.css';

const { Text } = Typography;

export interface IncidentDetailLinkedContextProps extends IncidentDetailLinkedContextData {}

export const IncidentDetailLinkedContext: FC<
  IncidentDetailLinkedContextProps
> = (props) => {
  const { t } = useTranslation('incidents');
  const { items } = useIncidentDetailLinkedContext(props);
  const chargerIconSrc = getPublicAssetUrl('assets/ev-charger.png');

  return (
    <section
      className={styles.section}
      aria-label={t(
        'detail.linkedContext.sectionLabel',
        'Linked site and asset',
      )}
    >
      {items.map((item) => {
        const openLinkLabel = t(
          item.openLinkLabelKey,
          item.openLinkLabelDefault,
        );
        const titleNode = item.href ? (
          <Link
            className={`${styles.title} ${styles.titleLink}`}
            to={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.title}
          </Link>
        ) : (
          <Text className={styles.title} ellipsis={{ tooltip: item.title }}>
            {item.title}
          </Text>
        );

        return (
          <div key={item.kind} className={styles.card}>
            <div className={styles.iconBox} aria-hidden="true">
              {item.kind === 'site' ? (
                <EnvironmentOutlined />
              ) : (
                <img
                  src={chargerIconSrc}
                  alt=""
                  className={styles.iconImage}
                  width={18}
                  height={18}
                />
              )}
            </div>

            <div className={styles.body}>
              <Text className={styles.label}>
                {t(item.labelKey, item.labelDefault)}
              </Text>
              {titleNode}
              {item.secondary ? (
                <Text
                  className={styles.secondary}
                  ellipsis={{ tooltip: item.secondary }}
                >
                  {item.secondary}
                </Text>
              ) : null}
            </div>

            {item.href ? (
              <Tooltip title={openLinkLabel}>
                <Link
                  to={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.openLink}
                  aria-label={openLinkLabel}
                >
                  <ExportOutlined aria-hidden />
                </Link>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </section>
  );
};
